import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { TreeTable } from 'primereact/treetable';
import { InputNumber } from 'primereact/inputnumber';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../../../app/z-store/store';
// Import Lucide React icons
import { List, FolderTree } from 'lucide-react';
import './ManageAccounts.css'; // Add this import for custom CSS styles

const ManageAccounts = () => {
    const [accounts, setAccounts] = useState([]);
    const [treeNodes, setTreeNodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [formData, setFormData] = useState({
        accountCode: '',
        accountName: '',
        accountType: '',
        accountBalance: 0,
        parentAccountId: null,
        accountLevel: 0,
        isFinal: false,
        isActive: true
    });
    const [selectedParent, setSelectedParent] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [expandedKeys, setExpandedKeys] = useState({});
    const [viewType, setViewType] = useState('tree'); // 'tree' or 'table'
    const selectedFacilityId = useFacilityStore(state => state.facilityId);
    const toast = useRef(null);

    const accountTypes = [
        { label: 'Asset', value: 'Asset' },
        { label: 'Liability', value: 'Liability' },
        { label: 'Equity', value: 'Equity' },
        { label: 'Revenue', value: 'Revenue' },
        { label: 'Expense', value: 'Expense' }
    ];

    useEffect(() => {
        if (selectedFacilityId) {
            fetchAccounts();
        }
    }, [selectedFacilityId]);

    // Function to update parent account balances for flat list view
    const updateParentAccountBalances = (accounts) => {
        // Create a map for quick lookup
        const accountMap = {};
        accounts.forEach(account => {
            accountMap[account._id] = { ...account, children: [] };
        });

        // Build parent-child relationships
        accounts.forEach(account => {
            if (account.parentAccountId && accountMap[account.parentAccountId]) {
                accountMap[account.parentAccountId].children.push(account._id);
            }
        });

        // Calculate sum of final child accounts recursively
        const calculateBalance = (accountId, visited = new Set()) => {
            // Prevent infinite recursion from circular relationships
            if (visited.has(accountId)) return 0;
            visited.add(accountId);

            const account = accountMap[accountId];
            let sum = 0;

            // Only include balances from final child accounts
            for (const childId of account.children) {
                const child = accountMap[childId];
                if (child.isFinal) {
                    // Add balance of this final child account
                    sum += child.accountBalance || 0;
                } else {
                    // Recursively calculate balances of non-final children
                    sum += calculateBalance(childId, new Set(visited));
                }
            }

            return sum;
        };

        // Update all accounts with calculated balances
        const result = accounts.map(account => {
            // Only non-final accounts' balances should be calculated
            if (!account.isFinal) {
                const calculatedBalance = calculateBalance(account._id);

                return {
                    ...account,
                    accountBalance: calculatedBalance
                };
            }

            // Leave final accounts with their original balances
            return account;
        });

        return result;
    };

    const fetchAccounts = async () => {
        try {
            setLoading(true);
            console.log('Fetching accounts for facility:', selectedFacilityId);
            const response = await makeRequest2(`/api/v1/gl-accounts/${selectedFacilityId}`, 'GET');
            console.log('Raw API response:', response);

            // Handle nested data structure
            let fetchedAccounts = [];
            if (response?.data?.data) {
                // Handle the nested data structure (response.data.data)
                fetchedAccounts = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            } else if (response?.data) {
                fetchedAccounts = Array.isArray(response.data) ? response.data : [response.data];
            } else if (Array.isArray(response)) {
                fetchedAccounts = response;
            }

            console.log('Processed accounts:', fetchedAccounts);

            // Process accounts to update the data structure for display in table view
            const processedAccounts = updateParentAccountBalances(fetchedAccounts);
            setAccounts(processedAccounts);

            if (fetchedAccounts.length === 0) {
                setTreeNodes([]);
                setLoading(false);
                return;
            }

            // Transform flat list to tree structure for TreeTable
            const nodes = transformToTreeNodes(fetchedAccounts);
            setTreeNodes(nodes);

            // Expand all top-level nodes by default
            const keys = {};
            nodes.forEach(node => {
                keys[node.key] = true;
            });
            setExpandedKeys(keys);
        } catch (error) {
            console.error('Error fetching accounts:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch accounts: ' + (error.message || 'Unknown error')
            });
        } finally {
            setLoading(false);
        }
    };

    // Calculate the sum of final child accounts' balances for each parent
    const calculateBalances = (node, visited = new Set()) => {
        // Prevent infinite recursion
        if (visited.has(node.key)) return 0;
        visited.add(node.key);

        let sum = 0;

        // Process all children
        for (const child of node.children) {
            if (child.isFinal) {
                // Add balance of this final child account
                sum += child.accountBalance || 0;
            } else {
                // Recursively calculate for non-final children
                sum += calculateBalances(child, new Set(visited));
            }
        }

        // Update the node's calculated balance and its data.accountBalance
        node.calculatedBalance = sum;
        node.data.accountBalance = sum;

        return sum;
    };

    // Enhanced transform function with better sorting and handling
    const transformToTreeNodes = (accounts) => {
        const accountMap = {};
        const rootNodes = [];

        // First, create a map of all accounts and identify root nodes
        accounts.forEach(account => {
            accountMap[account._id] = {
                ...account,
                key: account._id,
                data: {
                    accountCode: account.accountCode,
                    accountName: account.accountName,
                    accountType: account.accountType,
                    accountBalance: account.accountBalance,
                    accountLevel: account.accountLevel,
                    isFinal: account.isFinal ? 'Yes' : 'No',
                    isActive: account.isActive ? 'Yes' : 'No'
                },
                children: []
            };
        });

        // Then build the tree by adding children to their parents
        accounts.forEach(account => {
            if (account.parentAccountId && accountMap[account.parentAccountId]) {
                accountMap[account.parentAccountId].children.push(accountMap[account._id]);
            } else if (!account.parentAccountId) {
                // This is a root account
                rootNodes.push(accountMap[account._id]);
            } else {
                // Parent ID exists but parent not found - add as root node
                console.warn(`Parent account ${account.parentAccountId} not found for ${account.accountName}`);
                rootNodes.push(accountMap[account._id]);
            }
        });

        // Process all root nodes
        for (const node of rootNodes) {
            calculateBalances(node);
        }

        // Sort nodes at each level by account code
        const sortNodes = (nodes) => {
            nodes.sort((a, b) => a.accountCode.localeCompare(b.accountCode));
            nodes.forEach(node => {
                if (node.children && node.children.length > 0) {
                    sortNodes(node.children);
                }
            });
            return nodes;
        };

        return sortNodes(rootNodes);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleNumberChange = (e) => {
        // Ensure value is correctly handled as a number
        const value = typeof e.value !== 'undefined' ? e.value : 0;
        console.log('Number input changed:', e);
        console.log('Setting accountBalance to:', value);

        setFormData(prev => ({
            ...prev,
            accountBalance: value
        }));
    };

    const resetForm = (parentAccount = null) => {
        if (parentAccount) {
            setFormData({
                accountCode: '',
                accountName: '',
                accountType: parentAccount.accountType || '',
                accountBalance: 0,
                parentAccountId: parentAccount._id,
                accountLevel: parentAccount.accountLevel + 1,
                isFinal: false,
                isActive: true
            });
            setSelectedParent(parentAccount);
        } else {
            setFormData({
                accountCode: '',
                accountName: '',
                accountType: '',
                accountBalance: 0,
                parentAccountId: null,
                accountLevel: 0,
                isFinal: false,
                isActive: true
            });
            setSelectedParent(null);
        }
    };

    const addNewAccount = () => {
        resetForm();
        setShowDialog(true);
    };

    const addChildAccount = (parentAccount) => {
        // Check if parent is a final account
        if (parentAccount.isFinal) {
            toast.current.show({
                severity: 'error',
                summary: 'Not Allowed',
                detail: 'Cannot add child accounts to a final account'
            });
            return;
        }

        resetForm(parentAccount);
        setShowDialog(true);
    };

    const saveAccount = async () => {
        try {
            // Validate form
            if (!formData.accountCode || !formData.accountName || !formData.accountType) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'Please fill all required fields'
                });
                return;
            }

            setLoading(true);

            // Make sure accountBalance is a number
            const dataToSend = {
                ...formData,
                accountBalance: parseFloat(formData.accountBalance) || 0
            };

            console.log('Sending data to API:', dataToSend);

            try {
                // Make the API request
                await makeRequest2(
                    `/api/v1/gl-accounts/${selectedFacilityId}`,
                    'POST',
                    dataToSend
                );

                // If we reach this line, show success message regardless of returned data
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Account created successfully'
                });

                await fetchAccounts();
                setShowDialog(false);
            } catch (error) {
                // Check if the error actually contains a successful response
                // This handles the case where makeRequest2 throws for 201 responses
                if (error && error.message && error.message.includes('201')) {
                    // This is actually a success!
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Account created successfully'
                    });

                    await fetchAccounts();
                    setShowDialog(false);
                } else {
                    // This is a real error
                    throw error;
                }
            }
        } catch (error) {
            console.error('Error saving account:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: typeof error === 'string' ? error : error.message || 'Failed to save account'
            });
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const balanceTemplate = (rowData) => {
        return formatCurrency(rowData.accountBalance);
    };

    const actionsTemplate = (rowData) => {
        // Don't show the add child button for final accounts
        if (rowData.isFinal) {
            return (
                <div className="d-flex gap-2">
                    <span className="text-muted small">Final Account</span>
                </div>
            );
        }

        return (
            <div className="d-flex gap-2">
                <Button
                    icon="ti ti-plus"
                    className="p-button-rounded p-button-success p-button-sm"
                    onClick={() => addChildAccount(rowData)}
                    tooltip="Add Child Account"
                />
            </div>
        );
    };

    const treeBalanceTemplate = (node) => {
        return formatCurrency(node.data.accountBalance);
    };

    const treeActionsTemplate = (node) => {
        // Don't show the add child button for final accounts
        if (node.isFinal) {
            return (
                <div className="d-flex gap-2">
                    <span className="text-muted small">Final Account</span>
                </div>
            );
        }

        return (
            <div className="d-flex gap-2">
                <Button
                    icon="ti ti-plus"
                    className="p-button-rounded p-button-success p-button-sm"
                    onClick={() => addChildAccount(node)}
                    tooltip="Add Child Account"
                />
            </div>
        );
    };

    const renderHeader = () => {
        return (
            <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                    <div className="p-input-icon-left w-auto">
                        <i className="pi pi-search" />
                        <InputText
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder="Search accounts"
                            className="p-inputtext-sm"
                        />
                    </div>
                    <div className="btn-group">
                        <Button
                            className={`p-button-md ${viewType === 'tree' ? 'p-button-primary' : 'p-button-outlined'}`}
                            onClick={() => setViewType('tree')}
                            tooltip="Tree View"
                            icon={props => <FolderTree {...props} size={16} />}
                        />
                        <Button
                            className={`p-button-md ${viewType === 'table' ? 'p-button-primary' : 'p-button-outlined'}`}
                            onClick={() => setViewType('table')}
                            tooltip="Table View"
                            icon={props => <List {...props} size={16} />}
                        />
                    </div>
                </div>
                <Button
                    label="Add Root Account"
                    icon="pi pi-plus"
                    onClick={addNewAccount}
                />
            </div>
        );
    };

    const dialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setShowDialog(false)} />
            <Button label="Save" icon="pi pi-check" onClick={saveAccount} loading={loading} />
        </div>
    );

    const filterAccounts = (data) => {
        if (!searchText.trim()) {
            return data;
        }

        const searchLower = searchText.toLowerCase();
        return data.filter(account =>
            account.accountCode?.toLowerCase().includes(searchLower) ||
            account.accountName?.toLowerCase().includes(searchLower) ||
            account.accountType?.toLowerCase().includes(searchLower)
        );
    };

    // TreeTable node expansion handler
    const onToggle = (e) => {
        setExpandedKeys(e.value);
    };

    const dialogHeader = selectedParent
        ? `Add Child Account to ${selectedParent.accountName}`
        : "Add Root Account";

    // Custom row class for the DataTable based on row index
    const rowClassName = (data, options) => {
        return {
            'account-row-even': options.rowIndex % 2 === 0,
            'account-row-odd': options.rowIndex % 2 === 1
        };
    };

    // Custom row class for the TreeTable
    const treeRowClassName = (node) => {
        // We'll apply a different style based on the account type
        return {
            'account-type-asset': node.data.accountType === 'Asset',
            'account-type-liability': node.data.accountType === 'Liability',
            'account-type-equity': node.data.accountType === 'Equity',
            'account-type-revenue': node.data.accountType === 'Revenue',
            'account-type-expense': node.data.accountType === 'Expense',
        };
    };

    return (
        <div className="manage-accounts">
            <Toast ref={toast} />

            <div className="card mb-0">
                <div className="card-body">
                    {renderHeader()}

                    <div className="mt-3">
                        {viewType === 'tree' ? (
                            <TreeTable
                                value={treeNodes}
                                expandedKeys={expandedKeys}
                                onToggle={onToggle}
                                loading={loading}
                                emptyMessage="No accounts found"
                                tableStyle={{ minWidth: '50rem' }}
                                rowClassName={treeRowClassName}
                                className="accounts-tree-table"
                            >
                                <Column field="accountCode" header="Code" expander />
                                <Column field="accountName" header="Account Name" />
                                <Column field="accountType" header="Type" />
                                <Column field="accountBalance" header="Balance" body={treeBalanceTemplate} />
                                <Column field="accountLevel" header="Level" />
                                <Column field="isFinal" header="Final" />
                                <Column field="isActive" header="Active" />
                                <Column body={treeActionsTemplate} header="Actions" style={{ width: '10rem' }} />
                            </TreeTable>
                        ) : (
                            <DataTable
                                value={filterAccounts(accounts)}
                                loading={loading}
                                paginator
                                rows={10}
                                rowsPerPageOptions={[10, 25, 50]}
                                emptyMessage="No accounts found"
                                dataKey="_id"
                                className="p-datatable-sm accounts-data-table"
                                sortField="accountCode"
                                sortOrder={1}
                                rowClassName={rowClassName}
                                stripedRows
                            >
                                <Column field="accountCode" header="Code" sortable />
                                <Column field="accountName" header="Account Name" sortable />
                                <Column field="accountType" header="Type" sortable />
                                <Column field="accountBalance" header="Balance" body={balanceTemplate} sortable />
                                <Column field="accountLevel" header="Level" sortable />
                                <Column
                                    field="isFinal"
                                    header="Final"
                                    body={(rowData) => rowData.isFinal ? 'Yes' : 'No'}
                                    sortable
                                />
                                <Column
                                    field="isActive"
                                    header="Status"
                                    body={(rowData) => rowData.isActive ? 'Active' : 'Inactive'}
                                    sortable
                                />
                                <Column body={actionsTemplate} header="Actions" style={{ width: '10rem' }} />
                            </DataTable>
                        )}
                    </div>
                </div>
            </div>

            {/* Add Account Dialog */}
            <Dialog
                visible={showDialog}
                style={{ width: '550px' }}
                header={dialogHeader}
                modal
                className="p-fluid"
                footer={dialogFooter}
                onHide={() => setShowDialog(false)}
            >
                <div className="p-formgrid p-grid">
                    <div className="p-field p-col-12 mb-3">
                        <label htmlFor="accountCode">Account Code*</label>
                        <InputText
                            id="accountCode"
                            name="accountCode"
                            value={formData.accountCode}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="p-field p-col-12 mb-3">
                        <label htmlFor="accountName">Account Name*</label>
                        <InputText
                            id="accountName"
                            name="accountName"
                            value={formData.accountName}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="p-field p-col-12 mb-3">
                        <label htmlFor="accountType">Account Type*</label>
                        <Dropdown
                            id="accountType"
                            name="accountType"
                            value={formData.accountType}
                            options={accountTypes}
                            onChange={handleInputChange}
                            placeholder="Select Account Type"
                            disabled={!!selectedParent}
                        />
                        {selectedParent && (
                            <small className="text-muted">Child accounts inherit their parent's account type</small>
                        )}
                    </div>

                    <div className="p-field-checkbox p-col-12 mb-3">
                        <div className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="isFinal"
                                checked={formData.isFinal}
                                onChange={e => {
                                    setFormData(prev => ({
                                        ...prev,
                                        isFinal: e.target.checked,
                                        // Reset balance to 0 when switching between final and non-final account types
                                        accountBalance: e.target.checked ? prev.accountBalance : 0
                                    }));
                                }}
                            />
                            <label className="form-check-label" htmlFor="isFinal">
                                Final Account (cannot have child accounts)
                            </label>
                        </div>
                    </div>

                    {formData.isFinal && (
                        <div className="p-field p-col-12 mb-3">
                            <label htmlFor="accountBalance">Opening Balance</label>
                            <InputNumber
                                id="accountBalance"
                                name="accountBalance"
                                value={formData.accountBalance}
                                onValueChange={(e) => handleNumberChange(e)}
                                mode="decimal"
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                locale="en-US"
                            />
                        </div>
                    )}

                    <div className="p-field p-col-12 mb-3">
                        <label htmlFor="accountLevel">Account Level</label>
                        <InputNumber
                            id="accountLevel"
                            name="accountLevel"
                            value={formData.accountLevel}
                            disabled
                        />
                        <small className="text-muted">
                            {selectedParent ?
                                `Child of ${selectedParent.accountName} (Level ${selectedParent.accountLevel})` :
                                'Root level account'}
                        </small>
                    </div>

                    <div className="p-field-checkbox p-col-12 mb-3">
                        <div className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="isActive"
                                checked={formData.isActive}
                                onChange={e => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                            />
                            <label className="form-check-label" htmlFor="isActive">
                                Active
                            </label>
                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default ManageAccounts;